/**
 * FLIP-276. What the voice UI shows on the Realtime path, and the events it is derived from.
 *
 * Separate from `nova-realtime.model.ts` on purpose: that file is the client's view of OUR server
 * contract (FLIP-255 / FLIP-257), which is ours to change. This one describes OpenAI's server
 * events and the view state we reduce them into, which is not.
 */

/**
 * The same five states the legacy path uses, so the template and the minimised launcher do not
 * have to learn a second vocabulary. What changes on this path is where they come from: connection
 * and response events rather than measured loudness.
 *
 * `interrupted` keeps its legacy meaning - the user has taken the floor and Nova has stopped -
 * with one difference worth knowing. On the legacy path it was a pause, entered on a loudness
 * threshold and reversible if what followed turned out to be a backchannel. Here the server's
 * turn detection has already decided, so it is feedback rather than a question: it shows the user
 * they have the floor the moment they take it.
 */
export type NovaRealtimeVoiceState = "off" | "listening" | "thinking" | "speaking" | "interrupted";

/**
 * Everything the status bar, the caption and the waveform gate need, and nothing else.
 *
 * A plain immutable value rather than a set of observables: it is rebuilt on every event, the
 * consumer wants a consistent snapshot of all of it at once, and a component reading five streams
 * that update in the same tick is how you get a caption that disagrees with its own status line.
 */
export interface NovaRealtimeVoiceView {
  state: NovaRealtimeVoiceState;

  /**
   * Nova's own transcript of the audio she is producing, accumulated over the current response.
   *
   * Exact rather than estimated - it is the model's transcript of its own speech - which is what
   * retires `SPOKEN_CHARS_PER_SECOND` and `wordEndFractions` on this path. Already scrubbed of
   * `[[...]]` tags; see `scrubCaption`.
   */
  novaCaption: string;

  /**
   * What the user's words were transcribed as, for the current turn. DISPLAY ONLY.
   *
   * This comes from a separate ASR model (`whisper-1`, set server-side in
   * `Nova:Realtime:TranscriptionModel`), not from the Realtime model that actually heard the
   * audio, and OpenAI documents that the two can disagree. The decision recorded on FLIP-276 is to
   * show it anyway: seeing that you were heard is real reassurance for this population. It is
   * never persisted (the relay stores the server-side transcripts), never fed back into the
   * conversation, and read by nothing but the caption.
   */
  userCaption: string;

  /**
   * Whether Nova's audio has actually started for the current response.
   *
   * The status bar's honesty depends on this. `speaking` is entered when the transcript starts
   * arriving, which can precede the first sound; the legacy path had the same problem and solved
   * it by asking whether a chunk had begun playing. Here the answer is exact, because the WebRTC
   * transport emits `output_audio_buffer.started` when the audio genuinely begins.
   */
  audioStarted: boolean;

  /**
   * Whether a reply is in flight — `response.created` until `response.done`.
   *
   * This, not `audioStarted`, is the answer to "does Nova have the floor". A live session showed
   * `output_audio_buffer.stopped` arriving half a second into a reply that ran for another ten,
   * because the output buffer drains between chunks. Anything keyed on the audio boolean therefore
   * ends mid-sentence: the caption froze eight characters in and the status bar fell back to
   * "listening" while she was still talking.
   */
  responseActive: boolean;
}

/** The view before anything has happened. */
export const NOVA_REALTIME_VOICE_VIEW_INITIAL: NovaRealtimeVoiceView = {
  state: "off",
  novaCaption: "",
  userCaption: "",
  audioStarted: false,
  responseActive: false,
};

/**
 * The server event types this path reduces. Named constants rather than string literals scattered
 * through a switch, so a rename in OpenAI's GA naming is a one-line change here.
 *
 * These are the GA names. The beta names for the two transcript events (`response.audio_transcript.*`)
 * are accepted as aliases below: a session minted against a model that still emits them would
 * otherwise show no caption at all, which is a silent failure rather than a loud one.
 */
export const NOVA_REALTIME_EVENTS = {
  /** The user started talking, per the server's turn detection - not per our loudness guess. */
  speechStarted: "input_audio_buffer.speech_started",
  /** The user stopped. The turn is now Nova's, whether or not she has begun. */
  speechStopped: "input_audio_buffer.speech_stopped",
  /** ASR delta for the user's own words. Display only - see `userCaption`. */
  inputTranscriptDelta: "conversation.item.input_audio_transcription.delta",
  /** ASR final for the user's own words. */
  inputTranscriptDone: "conversation.item.input_audio_transcription.completed",
  /** A reply has been started. Nova has the floor; no audio yet. */
  responseCreated: "response.created",
  /** Nova's transcript of her own audio, arriving as she says it. */
  outputTranscriptDelta: "response.output_audio_transcript.delta",
  /** The final form of that transcript for this response. */
  outputTranscriptDone: "response.output_audio_transcript.done",
  /** WebRTC-only: the audio genuinely started coming out. */
  audioBufferStarted: "output_audio_buffer.started",
  /** WebRTC-only: the audio finished. */
  audioBufferStopped: "output_audio_buffer.stopped",
  /** WebRTC-only: the audio was dropped, which is what a barge-in does to it. */
  audioBufferCleared: "output_audio_buffer.cleared",
  /** The reply is over, however it ended. */
  responseDone: "response.done",
} as const;

/** Beta-era aliases, accepted so a caption cannot silently vanish. See NOVA_REALTIME_EVENTS. */
export const NOVA_REALTIME_EVENT_ALIASES: Readonly<Record<string, string>> = {
  "response.audio_transcript.delta": NOVA_REALTIME_EVENTS.outputTranscriptDelta,
  "response.audio_transcript.done": NOVA_REALTIME_EVENTS.outputTranscriptDone,
};
